import { isResource, type ProfileResource } from '@medplum/core'
import type {
  ClientApplication,
  Patient,
  Practitioner,
  Reference,
  RelatedPerson,
} from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { ExtendedClientApplication } from '@seen/core/models/clientApplication'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import type { AvatarResource, ExtendedProfile } from '@seen/core/models/profile'
import { ExtendedRelatedPerson } from '@seen/core/models/relatedPerson'
import { createReference } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { type ComponentProps, memo, type ReactNode } from 'react'
import { AvatarFallback } from '#src/components/core/Avatar/AvatarFallback.tsx'
import { AvatarImage } from '#src/components/core/Avatar/AvatarImage.tsx'
import { AvatarRoot } from '#src/components/core/Avatar/AvatarRoot.tsx'
import { nameInitials } from '#src/components/core/Avatar/nameInitials.ts'
import { useRead } from '#src/hooks/useRead.ts'

export interface AvatarProps extends ComponentProps<typeof AvatarRoot> {
  profile: AvatarResource | Reference | undefined
  children?: ReactNode
}

export const Avatar = memo(function Avatar({
  profile,
  children,
  ...props
}: AvatarProps): ReactNode {
  if (!profile) {
    return (
      <AvatarRoot {...props}>
        <AvatarFallback />
        {children}
      </AvatarRoot>
    )
  }
  if (isOfType(profile, 'Practitioner')) {
    return (
      <PractitionerAvatar
        practitioner={isResource(profile) ? createReference(profile) : profile}
        {...props}
      >
        {children}
      </PractitionerAvatar>
    )
  }
  if (isOfType(profile, 'Patient')) {
    return (
      <ParticipantAvatar
        participant={isResource(profile) ? createReference(profile) : profile}
        {...props}
      >
        {children}
      </ParticipantAvatar>
    )
  }
  if (isOfType(profile, 'RelatedPerson')) {
    return (
      <RelatedPersonAvatar
        relatedPerson={isResource(profile) ? createReference(profile) : profile}
        {...props}
      >
        {children}
      </RelatedPersonAvatar>
    )
  }
  if (isOfType(profile, 'ClientApplication')) {
    return (
      <ClientApplicationAvatar
        clientApp={isResource(profile) ? createReference(profile) : profile}
        {...props}
      >
        {children}
      </ClientApplicationAvatar>
    )
  }
  return (
    <AvatarRoot {...props}>
      <AvatarFallback>
        {getReferenceInitials(
          isResource(profile) ? createReference(profile) : profile
        )}
      </AvatarFallback>
      {children}
    </AvatarRoot>
  )
})

interface PractitionerAvatarProps extends Omit<
  ComponentProps<typeof ProfileAvatar>,
  'profile' | 'profileReference'
> {
  practitioner: Reference<Practitioner>
}

function PractitionerAvatar({
  practitioner: practitionerReference,
  ...props
}: PractitionerAvatarProps): ReactNode {
  const { data: practitioner } = useRead(
    ExtendedPractitioner,
    practitionerReference
  )
  return (
    <ProfileAvatar
      profile={practitioner}
      profileReference={practitionerReference}
      {...props}
    />
  )
}

interface ParticipantAvatarProps extends Omit<
  ComponentProps<typeof ProfileAvatar>,
  'profile' | 'profileReference'
> {
  participant: Reference<Patient>
}

function ParticipantAvatar({
  participant: participantReference,
  ...props
}: ParticipantAvatarProps): ReactNode {
  const { data: participant } = useRead(Participant, participantReference)
  return (
    <ProfileAvatar
      profile={participant}
      profileReference={participantReference}
      {...props}
    />
  )
}

interface RelatedPersonAvatarProps extends Omit<
  ComponentProps<typeof ProfileAvatar>,
  'profile' | 'profileReference'
> {
  relatedPerson: Reference<RelatedPerson>
}

function RelatedPersonAvatar({
  relatedPerson: relatedPersonReference,
  ...props
}: RelatedPersonAvatarProps): ReactNode {
  const { data: relatedPerson } = useRead(
    ExtendedRelatedPerson,
    relatedPersonReference
  )
  return (
    <ProfileAvatar
      profile={relatedPerson}
      profileReference={relatedPersonReference}
      {...props}
    />
  )
}

interface ProfileAvatarProps extends ComponentProps<typeof AvatarRoot> {
  profile: ExtendedProfile<ProfileResource> | undefined
  profileReference: Reference<ProfileResource>
}

function ProfileAvatar({
  profile,
  profileReference,
  children,
  ...props
}: ProfileAvatarProps): ReactNode {
  const photoUrl = profile?.photoUrl
  const couldHavePhoto = !profile || !!photoUrl

  return (
    <AvatarRoot {...props}>
      <AvatarImage src={photoUrl} />
      <AvatarFallback delayMs={couldHavePhoto ? 600 : undefined}>
        {profile?.initials ?? getReferenceInitials(profileReference)}
      </AvatarFallback>
      {children}
    </AvatarRoot>
  )
}

interface ClientApplicationAvatarProps extends ComponentProps<
  typeof AvatarRoot
> {
  clientApp: Reference<ClientApplication>
}

function ClientApplicationAvatar({
  clientApp: clientAppReference,
  children,
  ...props
}: ClientApplicationAvatarProps): ReactNode {
  const { data: clientApp } = useRead(
    ExtendedClientApplication,
    clientAppReference
  )

  return (
    <AvatarRoot {...props}>
      <AvatarFallback>
        {clientApp?.initials ?? getReferenceInitials(clientAppReference)}
      </AvatarFallback>
      {children}
    </AvatarRoot>
  )
}

function getReferenceInitials(reference: Reference): string {
  const label = referenceDisplay(reference)
  return label ? nameInitials(label) : ''
}
